﻿import { useState, useEffect, useRef } from "react";
import introJs from "intro.js";
import { useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { Settings as SettingsIcon } from "lucide-react";

import Fetching from "@/components/Fetching/Fetching";
import useFilterStore from "@/stores/useFilterStore";

import { getSettings } from "@/api/Maintainers/settings-api";
import SettingsHeader from "./components/SettingsHeader";
import SettingsSidebar from "./components/SettingsSidebar";
import SettingsSection from "./components/SettingsSection";
import { getIcon } from "./icon-map";

import styles from "./settings.module.scss";

const steps = [
	{
		element: "#configuration-customer",
		intro: "En esta sección podrá visualizar la configuración sus razones sociales, campos y bodegas. 🌿",
	},
	{
		element: "#configuration-users",
		intro: "Luego tenemos la configuración de usuarios, donde podrá visualizar y gestionar los usuarios y operadores. 🧑‍💼",
	},
	{
		element: "#configuration-business-center",
		intro: "En la sección de centro de costo podrá configurar sus centros de costos y la visualización en balanza. 🏢",
	},
	{
		element: "#sidebar_inventories",
		intro: "Desde aquí podrá acceder al inventario, donde podrás visualizar el stock de tus productos 📦",
	},
];

const Settings = () => {
	const { filter } = useFilterStore();
	const { data: settingsData, isLoading } = useQuery(["settings", filter.estate_id, filter.estate_parent_id], () =>
		getSettings(filter.estate_id, filter.estate_parent_id),
	);
	const location = useLocation();
	const introInstance = useRef(null);
	const navigate = useNavigate();

	const searchParams = new URLSearchParams(location.search);
	const sectionParam = searchParams.get("section") || "general";

	const [activeSection, setActiveSection] = useState(sectionParam);

	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const sectionParam = searchParams.get("section");
		if (sectionParam) {
			setActiveSection(sectionParam);
		}
	}, [location.search]);

	const handleSectionChange = (sectionKey) => {
		setActiveSection(sectionKey);
		navigate(
			{
				pathname: location.pathname,
				search: `?section=${sectionKey}`,
			},
			{ replace: true },
		);
	};

	const sidebarSections =
		settingsData?.sections?.map((section) => ({
			key: section.key,
			title: section.title,
			subtitle: section.subtitle,
			Icon: getIcon(section.icon),
			count: section.count,
		})) || [];

	const currentSection = settingsData?.sections?.find((section) => section.key === activeSection);
	const currentSectionWithIcons = currentSection
		? {
				...currentSection,
				cards: currentSection.cards.map((card) => ({
					key: card.key,
					title: card.title,
					description: card.description,
					to: card.path,
					Icon: getIcon(card.icon),
					count: card.count,
				})),
			}
		: null;

	useEffect(() => {
		if (!isLoading && location.state?.from === "tour") {
			document.getElementById("profile-dropdown").style.display = "none";

			const startTour = () => {
				introInstance.current = introJs();
				introInstance.current.setOptions({
					steps: steps,
					exitOnOverlayClick: false,
					exitOnEsc: false,
					showProgress: true,
					showBullets: true,
					prevLabel: "Anterior",
					nextLabel: "Siguiente",
					doneLabel: "Ir al inventario",
				});
				introInstance.current.start();

				introInstance.current.oncomplete(() => {
					navigate({
						pathname: "/inventario",
						state: { from: "tour" },
					});
				});
			};

			startTour();
		}
	}, [isLoading, location.state, navigate]);

	if (isLoading) {
		return <Fetching />;
	}

	if (!settingsData || !settingsData.sections || settingsData.sections.length === 0) {
		return (
			<div className={styles.layout}>
				<div className={styles.mainContent}>
					<div className={styles.emptyState}>
						<div className={styles.emptyIcon}>
							<SettingsIcon size={40} strokeWidth={1.5} />
						</div>
						<h2 className={styles.emptyTitle}>No hay configuraciones disponibles</h2>
						<p className={styles.emptyDescription}>
							Actualmente no tienes acceso a ninguna sección de configuración. Contacta con tu administrador si necesitas permisos adicionales.
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.layout}>
			<SettingsSidebar sections={sidebarSections} activeSection={activeSection} onSectionChange={handleSectionChange} />

			<div className={styles.mainContent}>
				<SettingsHeader />
				{currentSectionWithIcons && (
					<SettingsSection title={currentSectionWithIcons.title} subtitle={currentSectionWithIcons.subtitle} cards={currentSectionWithIcons.cards} />
				)}
			</div>
		</div>
	);
};

export default Settings;
